import { FC, useEffect, useState } from "react";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import _ from "lodash";
import { ActivityIndicator, ImageURISource, StyleProp, StyleSheet, TouchableOpacity, ViewStyle } from "react-native";
import * as DocumentPicker from "expo-document-picker";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdClose, PdImport } from "src/components/Icons";
import { SLImage } from "src/components";
import { tReactiveImageProps } from "src/components/ReactiveImage";

export type tPdImagePickerAction = "change" | "remove";

export type tImagePickerValue = {
  uri?: string;
  file?: File;
  action: tPdImagePickerAction;
};

export type tPdImagePickerProps = Omit<tReactiveImageProps, "style" | "source"> & {
  style?: StyleProp<ViewStyle> | undefined;
  source?: ImageURISource;
  onChange?: (value: tImagePickerValue) => void;
  placeholder?: string;
  showPlaceholder?: boolean;
  allowDelete?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
};

import "src/util/datePicker.css";
import { PdText } from "src/components/layout";

export const DateRangePicker: FC<ReactDatePickerProps> = (props) => {
  return <ReactDatePicker selectsRange inline {...props} />;
};

export const PdImagePicker: FC<tPdImagePickerProps> = ({
  style,
  source,
  onChange,
  placeholder,
  allowDelete,
  isLoading,
  isDisabled,
  ...rest
}) => {
  const [_source, setSource] = useState<ImageURISource>(source || {});
  const isControlled = !!source;

  const handleOnPress = async () => {
    if (!isDisabled) {
      const image = await DocumentPicker.getDocumentAsync({ type: "image/png, image/jpeg, image/webp" });

      if (image.type === "success") {
        if (isControlled) {
          setSource({ uri: image.uri });
        }
        onChange?.({ uri: image.uri, file: image.file, action: "change" });
      }
    }
  };

  const handleOnPressDelete = () => {
    if (isControlled) {
      setSource({});
    }
    onChange?.({ uri: undefined, file: undefined, action: "remove" });
  };

  const renderActionButton = () => {
    if (isLoading) {
      return <ActivityIndicator size="small" color={kColors.brandDark} style={styles.pdImagePickerActionButton} />;
    }

    if (allowDelete && _source.uri) {
      return (
        <TouchableOpacity onPress={handleOnPressDelete} style={styles.pdImagePickerActionButton}>
          <PdClose style={styles.pdImagePickerActionDeleteButtonIcon} color={kColors.brandDark} />
        </TouchableOpacity>
      );
    }

    return null;
  };

  useEffect(() => {
    if (source?.uri && !_source.uri) {
      setSource(source);
    }
  }, [_source, source]);

  return (
    <TouchableOpacity
      disabled={isDisabled}
      onPress={handleOnPress}
      style={[styles.pdImagePicker, isDisabled ? styles.pdImagePickerActionDisabled : {}, style]}
    >
      {renderActionButton()}
      {!_source.uri ? (
        <>
          <PdImport style={styles.pdImagePickerActionUploadButtonIcon} />
          {placeholder && <PdText variant="body-sm">{placeholder}</PdText>}
        </>
      ) : (
        <SLImage source={_source} {...rest} />
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  pdImagePicker: {
    borderRadius: kStyleConst.kSpacing,
  },
  pdImagePickerActionButton: {
    padding: 2 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  pdImagePickerActionDeleteButtonIcon: {
    height: 7,
    width: 7,
  },
  pdImagePickerActionDisabled: {
    opacity: 0.4,
  },
  pdImagePickerActionUploadButtonIcon: {
    height: 15,
    marginHorizontal: kStyleConst.kSpacing * 2,
    marginVertical: 5,
    width: 15,
  },
});
